<script setup lang="ts">
import { ANALYTICS_EVENT, useAnalytics } from '@/sharedComposables/useAnalytics'
import { storeToRefs } from 'pinia'
import { nextTick, ref } from 'vue'
import TutorialCard from './TutorialCard.vue'
import { useTutorialStore, type TutorialEntry } from './useTutorialStore'

defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  (e: 'updateNestedDialog', value: boolean): void
}>()

const { captureAnalyticsEvent } = useAnalytics()
const tutorialStore = useTutorialStore()
const { activeTutorialId, tutorials } = storeToRefs(tutorialStore)

/**
 * The tutorial videos run in an iframe, which is only hidden visually when the
 * user closes the dialog, so that we don't have a noticeable loading time when
 * first opening the dialog. This means that when 'closing' the dialog, the
 * video is still running, and audio is still playing.
 *
 * To stop audio when the dialog is closed, we increment this key.
 */
const key = ref(0)

const onSetActiveTutorial = (tutorial: TutorialEntry) => {
  captureAnalyticsEvent(ANALYTICS_EVENT.TUTORIAL_VIDEO_OPENED, { tutorial: tutorial.title })
  tutorialStore.setActiveTutorial(tutorial.id)
  tutorialStore.markTutorialReadState(tutorial.title, true)
  emit('updateNestedDialog', true)
}

const onClearActiveTutorial = (tutorialId: string) => {
  if (activeTutorialId.value !== tutorialId) {
    return
  }

  key.value++
  nextTick(() => {
    activeTutorialId.value = null
    emit('updateNestedDialog', false)
  })
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <p class="px-3 text-lg-15px-bold text-text">Popular tutorials</p>
    <div class="flex flex-col gap-1 px-2">
      <TutorialCard
        v-for="tutorial in tutorials"
        :key="tutorial.title"
        :tutorial="tutorial"
        :is-parent-open="isOpen"
        :active-tutorial-id="activeTutorialId"
        :show-badge="tutorial.new"
        @open="onSetActiveTutorial(tutorial)"
        @close="onClearActiveTutorial(tutorial.id)"
      />
    </div>
  </div>
</template>
